import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';
import { motion, AnimatePresence } from 'motion/react';
import { useLogEvent, AnalyticsIds, useAnalyticsSegmentationContext } from '../utils/useAnalytics';

export interface Dealer {
  id: string;
  name: string;
  availability: string;
  /** Optional: translation key for availability text (e.g. 'staticTranslations.selectDealership.availableInLocations') */
  availabilityTranslationKey?: string;
  /** Optional: params passed to the availability translation (e.g. { count: '5', city: 'UAE' }) */
  availabilityParams?: Record<string, string>;
  /** Hidden from the dealer selector and the Matching Vehicles dealer dropdown. */
  hidden?: boolean;
}

export interface DealerRegion {
  region: string;
  /** Optional: country/city key used to look up translated region heading via dealersInCity */
  cityKey?: string;
  dealers: Dealer[];
}

interface DealerSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: (dealerId: string) => void;
  isDarkMode: boolean;
  regions?: DealerRegion[];
}

const ALL_REGIONS: DealerRegion[] = [
  {
    region: 'Dealers in UAE',
    cityKey: 'UAE',
    dealers: [
      {
        id: 'al-gandhi',
        name: 'Al Gandhi Auto',
        availability: 'Available in 5 locations of UAE',
        availabilityTranslationKey: 'staticTranslations.selectDealership.availableInLocations',
        availabilityParams: { count: '5', city: 'UAE' },
      },
      {
        id: 'bin-hamoodah',
        name: 'Bin Hamoodah Auto',
        availability: 'Available in 3 locations of UAE',
        availabilityTranslationKey: 'staticTranslations.selectDealership.availableInLocations',
        availabilityParams: { count: '3', city: 'UAE' },
        // Hidden until BHA inventory is integrated in AutoIntel — delete this
        // line to bring it back in both the selector and the dropdown.
        hidden: true,
      },
    ],
  },
];

/** Selectable dealers per region — `hidden` dealers are left out everywhere. */
export const DEFAULT_REGIONS: DealerRegion[] = ALL_REGIONS
  .map(r => ({ ...r, dealers: r.dealers.filter(d => !d.hidden) }))
  .filter(r => r.dealers.length > 0);

/**
 * Maps the UI dealer slug (selectedDealer) to the AutoIntel dealer id used for
 * inventory filtering. When a dealer is selected, only its inventory is fetched;
 * if the API returns no data, the UI shows nothing.
 */
export const DEALER_AUTONTEL_ID_MAP: Record<string, string> = {
  'al-gandhi': 'aga',
  'bin-hamoodah': 'bha',
};

export const DealerSelectorDialog: React.FC<DealerSelectorDialogProps> = ({
  isOpen,
  onClose,
  onProceed,
  isDarkMode,
  regions = DEFAULT_REGIONS,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const { trackEvent } = useLogEvent();
  const { setSticky } = useAnalyticsSegmentationContext();
  const allDealers = regions.flatMap((r) => r.dealers);

  useEffect(() => {
    if (isOpen) {
      setSelectedId(null);
      trackEvent(AnalyticsIds.dealerModalView, { parent: 'initial' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // dealerModalClose is only for the X button. Backdrop/Esc still close the
  // dialog but don't fire it: the backdrop stays clickable during the exit
  // animation, which produced spurious repeat events after Confirm.
  const handleClose = () => {
    onClose();
  };

  const handleCloseButton = () => {
    if (!isOpen) return;
    trackEvent(AnalyticsIds.dealerModalClose, {
      parent: 'dealer',
      primaryTypeName: 'initial',
    });
    onClose();
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose]);

  const handleSelect = (dealerId: string) => {
    const dealer = allDealers.find((d) => d.id === dealerId);
    trackEvent(AnalyticsIds.dealerSelectInteract, {
      parent: 'initial',
      primaryTypeName: dealer?.name || dealerId,
    });
    setSelectedId(dealerId);
  };

  const handleProceed = () => {
    if (selectedId) {
      const dealer = allDealers.find((d) => d.id === selectedId);
      trackEvent(AnalyticsIds.dealerConfirmInteract, {
        parent: 'initial',
        primaryTypeName: dealer?.name || selectedId,
      });
      setSticky({ dealerName: dealer?.name || selectedId });
      onProceed(selectedId);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-[70]"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className={cn(
              'fixed z-[70] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
              'w-[90vw] max-w-[811px] rounded-[var(--brand-dialog-radius)] shadow-2xl overflow-hidden',
              isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#eaeaea]',
            )}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dealer-dialog-title"
          >
            {/* White header section */}
            <div className={cn(
              'px-5 sm:px-[53px] pt-[35px] pb-[28px] relative',
              isDarkMode ? 'bg-[#111111]' : 'bg-white',
            )}>
              <h2
                id="dealer-dialog-title"
                className={cn(
                  'text-[18px] sm:text-[24px] font-bold leading-tight mb-[8px] pr-10',
                  isDarkMode ? 'text-white' : 'text-[#282828]',
                )}
              >
                {t('staticTranslations.selectDealership.title', 'Select a Dealer')}
              </h2>
              <p className={cn(
                'text-[13px] sm:text-[16px] leading-normal',
                isDarkMode ? 'text-white/50' : 'text-[#4e4e4e]',
              )}>
                {t('staticTranslations.selectDealership.subtitle', 'Please select a dealer to find local matches similar to your configuration')}
              </p>

              {/* Close button */}
              <button
                onClick={handleCloseButton}
                className={cn(
                  'absolute top-[18px] right-[18px] w-[34px] h-[34px] rounded-full border flex items-center justify-center transition-colors',
                  isDarkMode
                    ? 'border-white/20 text-white/60 hover:bg-white/10 hover:text-white'
                    : 'border-[#c8c8c8] text-[#666] hover:bg-gray-100 hover:text-[#282828]',
                )}
                aria-label="Close"
              >
                <X className="w-[14px] h-[14px]" strokeWidth={1.8} />
              </button>
            </div>

            {/* Gray body section */}
            <div className="px-5 sm:px-[40px] pt-[26px] pb-[32px]">
              {regions.map((regionData) => {
                const regionHeading = regionData.cityKey
                  ? t(
                      'staticTranslations.selectDealership.dealersInCity',
                      regionData.region,
                      { city: t(`staticTranslations.countryNames.${regionData.cityKey}`, regionData.cityKey) },
                    )
                  : regionData.region;
                return (
                <div key={regionData.region}>
                  <p className={cn(
                    'text-[18px] font-medium mb-[18px]',
                    isDarkMode ? 'text-white/80' : 'text-[#282828]',
                  )}>
                    {regionHeading}
                  </p>

                  {/* Dealer cards row */}
                  <div className="flex flex-wrap gap-[16px] mb-[28px]">
                    {regionData.dealers.map((dealer) => {
                      const isSelected = selectedId === dealer.id;
                      return (
                        <button
                          key={dealer.id}
                          onClick={() => handleSelect(dealer.id)}
                          className={cn(
                            'text-left w-full sm:w-[278px] min-h-[95px] px-[18px] py-[18px] rounded-[var(--brand-card-radius-lg)] border transition-all',
                            isSelected
                              ? isDarkMode
                                ? 'bg-[#1a2a4a] border-[var(--brand-accent)] border-[1.5px]'
                                : 'bg-white border-[var(--brand-accent)] border-[1.5px]'
                              : isDarkMode
                              ? 'bg-[#222] border-[#333] hover:border-[#444]'
                              : 'bg-white border-transparent hover:border-gray-200',
                          )}
                        >
                          <p className={cn(
                            'text-[14px] font-semibold leading-snug mb-[6px]',
                            isDarkMode ? 'text-white' : 'text-[#282828]',
                          )}>
                            {dealer.name}
                          </p>
                          <p className={cn(
                            'text-[12px] italic leading-[1.45]',
                            isDarkMode ? 'text-white/40' : 'text-[#7d7d7d]',
                          )}>
                            {dealer.availabilityTranslationKey
                              ? t(
                                  dealer.availabilityTranslationKey,
                                  dealer.availability,
                                  dealer.availabilityParams
                                    ? Object.fromEntries(
                                        Object.entries(dealer.availabilityParams).map(([k, v]) => [
                                          k,
                                          k === 'city'
                                            ? t(`staticTranslations.countryNames.${v}`, v)
                                            : v,
                                        ])
                                      )
                                    : undefined,
                                )
                              : dealer.availability}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                );
              })}

              {/* CTA button — right-aligned */}
              <div className="flex justify-end">
                <button
                  onClick={handleProceed}
                  disabled={!selectedId}
                  className={cn(
                    'min-h-[52px] px-5 sm:px-[36px] rounded-[var(--brand-button-radius-sm)] flex items-center gap-3 sm:gap-[20px] text-[13px] sm:text-[15px] font-semibold transition-all w-full sm:w-auto justify-center sm:justify-start',
                    selectedId
                      ? 'bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white'
                      : isDarkMode
                      ? 'bg-[var(--brand-accent)]/30 text-white/40 cursor-not-allowed'
                      : 'bg-[var(--brand-accent)]/30 text-white/60 cursor-not-allowed',
                  )}
                >
                  {t('staticTranslations.selectDealership.proceed', 'Select Dealer and Proceed to Configurator')}
                  {isRTL ? <ArrowLeft className="w-[20px] h-[18px]" strokeWidth={2} /> : <ArrowRight className="w-[20px] h-[18px]" strokeWidth={2} />}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
